import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { absoluteUrl, softwareApplicationSchema } from '../seo';
import './Pricing.css';

const FREE_FEATURES = [
  'All UK & Irish races & meetings',
  'Core race card - horse, form, age, weight',
  'Trainer & jockey names',
  'Official Rating (OR)',
  'HorseStat Rating',
  'Select analysis columns',
];

const PAID_MAIN_FEATURES = [
  'Horse form and trend analysis for recent performances',
  'Detailed horse performance tables for class, distance, and going',
  'PRB and A/E metrics to measure true performance quality',
  'Pace map and course pace-bias insights',
  'Draw bias and stall IV indicators',
  'Trainer, jockey, and owner performance stats',
  'Sire and dam analysis by class, distance, course, and going',
  'Race filtering and runner highlights to focus your shortlist',
];

const plans = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Get started',
    price: null,
    currency: null,
    period: null,
    description: 'Every UK & Irish race with core race card data \u2014 no payment needed.',
    cta: 'Get Started For Free',
    ctaHref: 'https://app.horsestat.com',
    highlight: false,
  },
  {
    id: 'weekender',
    name: 'Weekender',
    tagline: '48-hour pass',
    price: '10',
    currency: '\u00a3',
    period: '48 hrs',
    description: 'Full access to all the features of HorseStat Pro, for 48 hours.',
    cta: 'Get Weekend Pass',
    ctaHref: 'https://app.horsestat.com',
    highlight: false,
  },
  {
    id: 'pro',
    name: 'HorseStat Pro',
    tagline: 'Best value',
    price: '14.99',
    currency: '\u00a3',
    period: 'month',
    description: 'Unlimited daily access to all 22 analysis columns. The full edge, every day.',
    cta: 'Go Pro',
    ctaHref: 'https://app.horsestat.com',
    highlight: true,
  },
];

function CheckIcon({ highlight }) {
  return (
    <svg
      className={`check-icon${highlight ? ' check-icon--highlight' : ''}`}
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 8l3.5 3.5L13 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PlanCard({ plan }) {
  const isPaid = plan.id !== 'free';

  return (
    <div className={`plan-card${plan.highlight ? ' plan-card--pro' : ''}`}>
      {plan.highlight && <div className="plan-badge">BEST VALUE</div>}

      <div className="plan-header">
        <span className="plan-tagline">{plan.tagline}</span>
        <h2 className="plan-name">{plan.name}</h2>
        <p className="plan-desc">{plan.description}</p>
      </div>

      <div className="plan-price-block">
        {plan.price ? (
          <div className="plan-price">
            <span className="plan-currency">{plan.currency}</span>
            <span className="plan-amount">{plan.price}</span>
            <span className="plan-period">/ {plan.period}</span>
          </div>
        ) : (
          <div className="plan-price">
            <span className="plan-amount plan-amount--free">Free</span>
          </div>
        )}
      </div>

      <a
        href={plan.ctaHref}
        className={`plan-cta${plan.highlight ? ' plan-cta--pro' : ''}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {plan.cta}
      </a>

      <ul className="plan-features">
        {isPaid && (
          <li className="plan-feature-intro">All free features plus:</li>
        )}
        {(isPaid ? PAID_MAIN_FEATURES : FREE_FEATURES).map((f) => (
          <li key={f}>
            <CheckIcon highlight={isPaid} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pricing() {
  return (
    <div className="pricing-page">
      <Helmet>
        <title>Pricing | HorseStat</title>
        <meta name="description" content="HorseStat pricing — free forever with full racecards, or upgrade to Pro for complete analytics. Flexible weekend passes and monthly subscriptions available." />
        <link rel="canonical" href={absoluteUrl('/pricing')} />
        <meta property="og:title" content="Pricing | HorseStat" />
        <meta property="og:description" content="HorseStat pricing — free forever with full racecards, or upgrade to Pro for complete analytics. Flexible weekend passes and monthly subscriptions available." />
        <meta property="og:url" content={absoluteUrl('/pricing')} />
        <meta property="og:image" content={`${absoluteUrl('/social/horsestat-card.png')}`} />
        <script type="application/ld+json">{JSON.stringify(softwareApplicationSchema())}</script>
        <meta name="twitter:title" content="Pricing | HorseStat" />
        <meta name="twitter:description" content="HorseStat pricing — free forever with full racecards, or upgrade to Pro for complete analytics. Flexible weekend passes and monthly subscriptions available." />
      </Helmet>
      <section className="pricing-hero">
        <div className="container">
          <h1>
            Simple,{' '}
            <span className="hs-highlight">transparent pricing</span>
          </h1>
          <p className="pricing-hero-sub">
            Start free and upgrade when you want the full picture.
            No hidden fees, cancel anytime.
          </p>
        </div>
      </section>

      <section className="pricing-plans-section">
        <div className="pricing-plans-wrap">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </section>

      <section className="pricing-faq">
        <div className="container">
          <p>
            Have questions? Visit our{' '}
            <Link to="/help">Help Centre</Link>
            {' '}or read about{' '}
            <Link to="/features">what&apos;s included in each plan.</Link>
          </p>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
